import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  Users, FileText, Building2, Euro, AlertTriangle, 
  CheckCircle, Clock, ArrowRight, FileCheck, Briefcase,
  TrendingUp, Pause, Receipt
} from 'lucide-react';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';

// KPI Card cliccabile
const KPICard = ({ title, value, subtitle, icon: Icon, link, color = '#0078C8' }) => (
  <Link to={link} className="block group">
    <Card className="card-abitalo hover:shadow-lg transition-all cursor-pointer border-l-4" style={{ borderLeftColor: color }}>
      <CardContent className="pt-4 pb-4 px-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="text-[10px] sm:text-xs text-[#737373] uppercase tracking-wide font-medium whitespace-nowrap overflow-hidden text-ellipsis">{title}</p>
            <p className="text-xl sm:text-2xl font-bold mt-1 whitespace-nowrap" style={{ color }}>{value}</p>
            {subtitle && <p className="text-[10px] sm:text-xs text-[#737373] mt-1 whitespace-nowrap overflow-hidden text-ellipsis">{subtitle}</p>}
          </div>
          <div 
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" 
            style={{ backgroundColor: `${color}15` }}
          >
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  </Link>
);

// Bottleneck Alert Item
const BottleneckItem = ({ item }) => {
  const severityConfig = {
    high: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-500', dot: 'bg-red-500' },
    warning: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-500', dot: 'bg-amber-500' },
    info: { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-500', dot: 'bg-blue-500' }
  };
  
  const config = severityConfig[item.severity] || severityConfig.info;
  
  return (
    <Link 
      to={item.link} 
      className={`flex items-center justify-between p-3 rounded-lg border ${config.bg} ${config.border} hover:shadow-sm transition-all group`}
    >
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${config.dot}`} />
        <span className="text-sm font-medium text-[#3C3C3C]">{item.label}</span>
        <span className={`text-sm font-bold ${config.icon}`}>
          {item.count}
          {item.value > 0 && ` (${formatCurrency(item.value)})`}
        </span>
      </div>
      <ArrowRight className="w-4 h-4 text-[#737373] group-hover:translate-x-1 transition-transform" />
    </Link>
  );
};

// Pipeline Step
const PipelineStep = ({ step, isLast }) => {
  const hasValue = step.value > 0;
  const isActive = step.count > 0;
  
  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <div className="text-center min-w-[60px] sm:min-w-[80px] lg:min-w-[90px]">
        <div className={`text-xl sm:text-2xl font-bold ${isActive ? 'text-[#0078C8]' : 'text-[#BFBFBF]'}`}>
          {step.count}
        </div>
        <div className={`text-[11px] sm:text-xs leading-tight mt-0.5 ${isActive ? 'text-[#3C3C3C] font-medium' : 'text-[#737373]'}`}>
          {step.label}
        </div>
        {hasValue && (
          <div className="text-[10px] sm:text-xs text-[#008A3D] font-semibold mt-1">
            {formatCurrency(step.value)}
          </div>
        )}
      </div>
      {!isLast && (
        <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-[#BFBFBF] flex-shrink-0" />
      )}
    </div>
  );
};

// Stat Row for sections
const StatRow = ({ label, count, value, showValue = true }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-gray-100 last:border-0">
    <span className="text-sm text-[#3C3C3C]">{label}</span>
    <div className="flex items-center gap-3">
      <span className="text-sm font-semibold text-[#3C3C3C]">{count}</span>
      {showValue && value !== undefined && (
        <span className="text-sm text-[#008A3D] font-semibold min-w-[100px] text-right">
          {formatCurrency(value)}
        </span>
      )}
    </div>
  </div>
);

const StatsPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Redirect se non admin
  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      toast.error('Accesso non autorizzato');
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/statistics/operational-summary');
      if (response.data) {
        setData(response.data);
      }
    } catch (err) {
      console.error('Error loading operational summary:', err);
      setError('Errore nel caricamento dei dati');
      toast.error('Errore nel caricamento delle statistiche');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) return null;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="stats-page-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#FF7819] mx-auto mb-3"></div>
          <p className="text-[#737373]">Caricamento statistiche...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="stats-page-error">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-[#3C3C3C] font-medium">{error || 'Dati non disponibili'}</p>
          <button 
            onClick={loadData}
            className="mt-3 px-4 py-2 bg-[#FF7819] text-white rounded-lg hover:bg-[#E56A10] transition-colors text-sm"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  const { kpis, bottlenecks, pipeline, estimates_contracts, projects_payments } = data;

  return (
    <div className="space-y-5 animate-fade-in" data-testid="stats-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-gradient-to-br from-[#0078C8] to-[#00A3E0] rounded-xl flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[#3C3C3C]">Statistiche Operative</h1>
          <p className="text-xs text-[#737373]">Controllo quotidiano di lead, preventivi, contratti, cantieri e pagamenti</p>
        </div>
      </div>

      {/* ROW 1: KPI Operativi */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-section">
        <KPICard 
          title="Nuovi Lead" 
          value={kpis.new_leads.count}
          icon={Users}
          link={kpis.new_leads.link}
          color="#0078C8"
        />
        <KPICard 
          title="Lead Attivi" 
          value={kpis.active_leads.count}
          icon={Briefcase}
          link={kpis.active_leads.link}
          color="#FF7819"
        />
        <KPICard 
          title="Preventivi" 
          value={kpis.open_estimates.count}
          subtitle={kpis.open_estimates.value > 0 ? formatCurrency(kpis.open_estimates.value) : null}
          icon={FileText}
          link={kpis.open_estimates.link}
          color="#B38C00"
        />
        <KPICard 
          title="Da Validare" 
          value={kpis.contracts_to_validate.count}
          icon={FileCheck}
          link={kpis.contracts_to_validate.link}
          color="#9333EA"
        />
        <KPICard 
          title="Cantieri" 
          value={kpis.active_projects.count}
          subtitle={`SAL: ${kpis.active_projects.avg_sal}%`}
          icon={Building2}
          link={kpis.active_projects.link}
          color="#008A3D"
        />
        <KPICard 
          title="Da Fatturare" 
          value={formatCurrency(kpis.to_invoice.value)}
          subtitle={`${kpis.to_invoice.count} milestone`}
          icon={Receipt}
          link={kpis.to_invoice.link}
          color="#CC0000"
        />
      </div>

      {/* ROW 2: Colli di Bottiglia */}
      {bottlenecks && bottlenecks.length > 0 && (
        <Card className="card-abitalo" data-testid="bottlenecks-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              Colli di Bottiglia
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {bottlenecks.map((item, idx) => (
              <BottleneckItem key={item.type || idx} item={item} />
            ))}
          </CardContent>
        </Card>
      )}

      {/* ROW 3: Pipeline Commerciale */}
      <Card className="card-abitalo" data-testid="pipeline-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#0078C8]" />
            Pipeline Commerciale
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between overflow-x-auto pb-2 gap-1">
            {pipeline.map((step, idx) => (
              <PipelineStep 
                key={step.status} 
                step={step} 
                isLast={idx === pipeline.length - 1}
              />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* ROW 4: Due Colonne - Preventivi/Contratti e Cantieri/Pagamenti */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Preventivi & Contratti */}
        <Card className="card-abitalo" data-testid="estimates-contracts-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#FF7819]" />
              Preventivi & Contratti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatRow 
              label="Preventivi aperti" 
              count={estimates_contracts.estimates_open.count} 
              value={estimates_contracts.estimates_open.value}
            />
            <StatRow 
              label="Preventivi validati" 
              count={estimates_contracts.estimates_validated.count} 
              value={estimates_contracts.estimates_validated.value}
            />
            <StatRow 
              label="Preventivi convertiti" 
              count={estimates_contracts.estimates_converted.count} 
              value={estimates_contracts.estimates_converted.value}
            />
            <StatRow 
              label="Contratti firmati" 
              count={estimates_contracts.contracts_signed.count} 
              value={estimates_contracts.contracts_signed.value}
            />
            <StatRow 
              label="Contratti da validare" 
              count={estimates_contracts.contracts_to_validate.count}
              showValue={false}
            />
            <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-200">
              <span className="text-sm font-medium text-[#3C3C3C]">Tasso conversione</span>
              <span className="text-lg font-bold text-[#008A3D]">
                {estimates_contracts.conversion_rate}%
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Cantieri & Pagamenti */}
        <Card className="card-abitalo" data-testid="projects-payments-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="w-4 h-4 text-[#008A3D]" />
              Cantieri & Pagamenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            <StatRow 
              label="Cantieri attivi" 
              count={projects_payments.projects_active.count} 
              value={projects_payments.projects_active.value}
            />
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-sm text-[#3C3C3C]">SAL medio</span>
              <span className="text-sm font-semibold text-[#0078C8]">
                {projects_payments.projects_active.avg_sal}%
              </span>
            </div>
            <StatRow 
              label="Cantieri sospesi" 
              count={projects_payments.projects_suspended.count}
              showValue={false}
            />
            <StatRow 
              label="Cantieri completati" 
              count={projects_payments.projects_completed.count}
              showValue={false}
            />
            
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-xs font-medium text-[#737373] uppercase tracking-wide mb-2">Pagamenti</p>
              <StatRow 
                label="Da fatturare" 
                count={projects_payments.to_invoice.count} 
                value={projects_payments.to_invoice.value}
              />
              <StatRow 
                label="Da incassare" 
                count={projects_payments.invoiced_pending.count} 
                value={projects_payments.invoiced_pending.value}
              />
              <StatRow 
                label="Incassato" 
                count={projects_payments.received.count} 
                value={projects_payments.received.value}
              />
              <StatRow 
                label="Non sbloccato" 
                count={projects_payments.not_unlocked.count} 
                value={projects_payments.not_unlocked.value}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StatsPage;
